/**
 * Billing API client — plan 19 Fase 1 (top-up point via Mayar).
 *
 * Thin wrapper atas:
 *   - GET  /v1/billing/packages        — daftar paket aktif (harga dari server)
 *   - POST /v1/billing/topup           — buat invoice Mayar (idempoten)
 *   - GET  /v1/billing/wallet          — saldo point
 *   - GET  /v1/billing/transactions    — gabungan order + ledger (paginated)
 *
 * Invariant: client TIDAK pernah mengirim harga/jumlah credit — hanya
 * `package_code` + `idempotency_key`. Harga selalu dari tabel credit_package
 * di server (doc 15 §6.2).
 */

import { apiFetch } from './client';

// ----------------------------------------------------------------------------
// Types (mirror response server)
// ----------------------------------------------------------------------------

export type CreditPackage = {
  code: string;
  credit_amount: number;
  price_idr: number;
  label: string;
};

export type TopupResult = {
  reference_code: string;
  payment_url: string | null;
  status: string;
  /** true bila idempotency_key sudah pernah dipakai — order lama dikembalikan. */
  replay?: boolean;
};

export type WalletState = {
  balance: number;
  /** true bila akun punya entitlement katalog premium aktif (server-side). */
  premium: boolean;
};

export type BillingTransactionItem = {
  /** 'order' = payment_order; 'ledger' = credit_ledger. */
  row_type: 'order' | 'ledger';
  id: number;
  /** order: 'credit_topup' | ...; ledger: 'topup' | 'debit' | 'refund' | 'adjustment'. */
  kind: string;
  /** order: reference_code (FP-…); ledger: feature_code. */
  ref: string | null;
  amount_idr: number | null;
  credit: number | null;
  /** order saja: 'pending' | 'paid' | 'failed' | 'expired' | 'refunded'. */
  status: string | null;
  created_at: string;
};

export type BillingTransactionPage = {
  items: BillingTransactionItem[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
};

// ----------------------------------------------------------------------------
// GET /v1/billing/packages
// ----------------------------------------------------------------------------

type GetPackagesResponse = { ok: true; packages: CreditPackage[] };

export async function getBillingPackages(): Promise<CreditPackage[]> {
  const response = await apiFetch<GetPackagesResponse>('/v1/billing/packages');
  return response.packages ?? [];
}

// ----------------------------------------------------------------------------
// POST /v1/billing/topup
// Throws ApiError code 'AccountRequired' (403) untuk guest — UI mengarahkan
// ke login Google.
// ----------------------------------------------------------------------------

type CreateTopupResponse = { ok: true } & TopupResult;

export async function createTopup(
  packageCode: string,
  idempotencyKey: string,
): Promise<TopupResult> {
  const response = await apiFetch<CreateTopupResponse>('/v1/billing/topup', {
    method: 'POST',
    body: { package_code: packageCode, idempotency_key: idempotencyKey },
  });
  return {
    reference_code: response.reference_code,
    payment_url: response.payment_url,
    status: response.status,
    replay: response.replay,
  };
}

// ----------------------------------------------------------------------------
// GET /v1/billing/wallet
// ----------------------------------------------------------------------------

type GetWalletResponse = { ok: true } & WalletState;

export async function getWallet(): Promise<WalletState> {
  const response = await apiFetch<GetWalletResponse>('/v1/billing/wallet');
  return { balance: response.balance ?? 0, premium: response.premium ?? false };
}

// ----------------------------------------------------------------------------
// GET /v1/billing/transactions?page=1&pageSize=20
// ----------------------------------------------------------------------------

type GetTransactionsResponse = {
  ok: true;
  items: BillingTransactionItem[];
  page: number;
  pageSize: number;
  total: number;
};

export async function getBillingTransactions(
  page = 1,
  pageSize = 20,
): Promise<BillingTransactionPage> {
  const response = await apiFetch<GetTransactionsResponse>(
    `/v1/billing/transactions?page=${page}&pageSize=${pageSize}`,
  );
  const items = response.items ?? [];
  return {
    items,
    page: response.page,
    pageSize: response.pageSize,
    total: response.total ?? 0,
    hasMore: page * response.pageSize < (response.total ?? 0),
  };
}
